import React, { memo, ReactNode, useMemo } from 'react';

import * as C from './Charts.styled';

/** Ступеней тона: 0 — нет значения; 1…4 — от бледной к насыщенной. */
export const HEAT_TONE_STEPS = 4;

export interface IHeatAxis {
  key: string;
  label: ReactNode;
  /** Выделенная колонка (LearnIT среди конкурентов). */
  highlight?: boolean;
}

export interface IHeatCell {
  /** Число для тона; `null` — «—», тона нет. */
  value: number | null;
  /** Подпись ячейки: «12 %». */
  text: string;
  /** Вторая строка ячейки: «из 46». */
  secondary?: string | null;
  /** Незрелая / мало данных — приглушённо, без тона. */
  muted?: boolean;
  /** Подсказка ячейки. */
  title?: string;
}

export type HeatToneMode = 'share' | 'rank';

export const heatCellKey = (row: string, col: string): string =>
  `${row}|${col}`;

/**
 * Тоны ячеек. `share` — доля от НАИБОЛЬШЕГО значения таблицы (самая
 * насыщенная клетка одна на таблицу, как у матрицы «канал × курс»); `rank` —
 * место в строке среди колонок (кто где сильнее: лучший в строке — самый
 * насыщенный; равные — одним тоном). Приглушённые, пустые и нули — 0
 * (ноль печатается «0», пустое — «—»).
 */
export const heatMatrixTones = (
  rows: ReadonlyArray<IHeatAxis>,
  cols: ReadonlyArray<IHeatAxis>,
  cells: Readonly<Record<string, IHeatCell | undefined>>,
  mode: HeatToneMode = 'share',
): Record<string, number> => {
  const tones: Record<string, number> = {};
  const live = (row: string, col: string) => {
    const cell = cells[heatCellKey(row, col)];
    return cell && !cell.muted && cell.value !== null && cell.value > 0
      ? cell.value
      : null;
  };
  if (mode === 'share') {
    const max = Math.max(
      0,
      ...rows.flatMap((row) => cols.map((col) => live(row.key, col.key) ?? 0)),
    );
    rows.forEach((row) =>
      cols.forEach((col) => {
        const value = live(row.key, col.key);
        tones[heatCellKey(row.key, col.key)] =
          value === null || max <= 0
            ? 0
            : Math.min(
                HEAT_TONE_STEPS,
                Math.max(1, Math.ceil((value / max) * HEAT_TONE_STEPS)),
              );
      }),
    );
    return tones;
  }
  rows.forEach((row) => {
    const ranked = cols
      .map((col) => ({ col: col.key, value: live(row.key, col.key) }))
      .filter((item): item is { col: string; value: number } =>
        Boolean(item.value),
      )
      .sort((a, b) => b.value - a.value);
    cols.forEach((col) => {
      tones[heatCellKey(row.key, col.key)] = 0;
    });
    // Место — с учётом ничьих (повторное ревью 0103, A6): равные значения
    // — один тон; место = сколько значений строго больше.
    ranked.forEach((item) => {
      const place = ranked.filter((other) => other.value > item.value).length;
      tones[heatCellKey(row.key, item.col)] = Math.max(
        1,
        HEAT_TONE_STEPS - place,
      );
    });
  });
  return tones;
};

export interface IHeatMatrixProps {
  rows: ReadonlyArray<IHeatAxis>;
  cols: ReadonlyArray<IHeatAxis>;
  cells: Readonly<Record<string, IHeatCell | undefined>>;
  mode?: HeatToneMode;
  /** Заголовок колонки строк. */
  cornerLabel: ReactNode;
  /** Подпись таблицы для чтеца экрана. */
  caption: string;
  onCellClick?: (row: string, col: string) => void;
  /** Подпись кнопки ячейки для чтеца: «Meta × Python: 12». */
  cellLabel?: (row: string, col: string, cell: IHeatCell) => string;
}

/**
 * ТЗ 0103 — тепловая матрица (обобщение «канал × курс»): тон по доле от
 * максимума или по месту в строке, выделенная колонка, клик по ячейке,
 * «—» для пустых, приглушённые «ещё зреет».
 */
export const HeatMatrix = memo(
  ({
    rows,
    cols,
    cells,
    mode = 'share',
    cornerLabel,
    caption,
    onCellClick,
    cellLabel,
  }: IHeatMatrixProps) => {
    const tones = useMemo(() => heatMatrixTones(rows, cols, cells, mode), [
      rows,
      cols,
      cells,
      mode,
    ]);
    return (
      <C.HeatScroll>
        <C.HeatTable>
          <C.SrCaption>{caption}</C.SrCaption>
          <thead>
            <tr>
              <C.HeatCorner scope="col">{cornerLabel}</C.HeatCorner>
              {cols.map((col) => (
                <C.HeatHead
                  key={col.key}
                  scope="col"
                  $highlight={col.highlight}
                >
                  {col.label}
                </C.HeatHead>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <C.HeatRowHead scope="row">{row.label}</C.HeatRowHead>
                {cols.map((col) => {
                  const key = heatCellKey(row.key, col.key);
                  const cell = cells[key];
                  const tone = tones[key] ?? 0;
                  if (!cell || cell.value === null) {
                    return (
                      <C.HeatCell key={col.key} $tone={0}>
                        <C.HeatEmpty>—</C.HeatEmpty>
                      </C.HeatCell>
                    );
                  }
                  const content = (
                    <>
                      {cell.text}
                      {cell.secondary && (
                        <C.HeatSecondary>{cell.secondary}</C.HeatSecondary>
                      )}
                    </>
                  );
                  return (
                    <C.HeatCell
                      key={col.key}
                      $tone={tone}
                      $muted={cell.muted}
                      $highlight={col.highlight}
                      title={cell.title}
                    >
                      {onCellClick ? (
                        <C.HeatButton
                          type="button"
                          aria-label={
                            cellLabel
                              ? cellLabel(row.key, col.key, cell)
                              : undefined
                          }
                          onClick={() => onCellClick(row.key, col.key)}
                        >
                          {content}
                        </C.HeatButton>
                      ) : (
                        <C.HeatValue>{content}</C.HeatValue>
                      )}
                    </C.HeatCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </C.HeatTable>
      </C.HeatScroll>
    );
  },
);

HeatMatrix.displayName = 'HeatMatrix';
